import React from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ContactForm from '../components/ContactForm';
import CTABanner from '../components/CTABanner';
import { MapPin, CheckCircle, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';

const locationData = {
  vancouver: {
    city: 'Vancouver',
    region: 'Metro Vancouver',
    title: 'Temp Agency Vancouver - WorkNation Labour Ltd | Temporary Workers Vancouver BC',
    meta: 'Need temp workers in Vancouver? WorkNation Labour Ltd provides construction, warehouse, food processing & cold storage workers in Vancouver BC. 24-48 hour placement. WCB compliant. Call today.',
    canonical: 'https://worknation.ca/locations/vancouver',
    hero: 'Temp Agency in Vancouver, BC',
    intro: 'Looking for temporary workers in Vancouver? WorkNation Labour Ltd is a staffing agency based in Vancouver, British Columbia, providing employers with qualified, WCB-compliant workers in 24-48 hours. Whether you need construction labourers for a downtown build, warehouse staff in East Vancouver, or food processing workers near the port — we deliver.',
    areas: ['Downtown Vancouver', 'East Vancouver', 'South Vancouver', 'West Side', 'Mount Pleasant', 'Kitsilano', 'Commercial Drive', 'Gastown', 'Yaletown'],
    industries: 'From the construction boom in downtown Vancouver to the warehouses and distribution hubs along the Clark Drive corridor, Vancouver employers rely on WorkNation to fill staffing gaps fast. We supply workers for construction sites, fulfillment centres, food processing plants, cold storage facilities, and transportation operations across the city.',
    stats: 'Vancouver is the economic heart of British Columbia, with thousands of active construction projects, a thriving logistics sector driven by the Port of Vancouver, and growing food processing and cold storage operations. Demand for temporary workers in Vancouver continues to rise — and WorkNation is here to meet it.',
  },
  surrey: {
    city: 'Surrey',
    region: 'South Fraser',
    title: 'Temp Agency Surrey - WorkNation Labour Ltd | Temporary Workers Surrey BC',
    meta: 'Need temp workers in Surrey BC? WorkNation Labour Ltd provides construction, warehouse, food processing workers in Surrey. Fast placement, WCB compliant. Contact us today.',
    canonical: 'https://worknation.ca/locations/surrey',
    hero: 'Temp Agency in Surrey, BC',
    intro: 'Need temporary workers in Surrey? WorkNation Labour Ltd provides fast, reliable staffing solutions for employers across Surrey and the South Fraser region. From the booming construction sector in Cloverdale and Fleetwood to the warehouse and logistics hubs along the Highway 10 corridor — we supply the workers your business needs.',
    areas: ['Whalley', 'Guildford', 'Fleetwood', 'Cloverdale', 'Newton', 'South Surrey', 'Panorama Ridge', 'Sullivan'],
    industries: 'Surrey is one of the fastest-growing cities in Canada, with massive residential and commercial construction, expanding industrial parks, and a growing food processing sector. WorkNation supplies construction labourers, warehouse workers, forklift operators, food processing staff, and drivers to employers across Surrey.',
    stats: 'Surrey\'s population and economy are growing rapidly, driving demand for skilled and general labour. New developments in City Centre, the Campbell Heights industrial area, and the expanding logistics corridor make Surrey one of BC\'s hottest markets for temporary staffing.',
  },
  burnaby: {
    city: 'Burnaby',
    region: 'Metro Vancouver',
    title: 'Temp Agency Burnaby - WorkNation Labour Ltd | Temporary Workers Burnaby BC',
    meta: 'Need temp workers in Burnaby BC? WorkNation Labour Ltd provides construction, warehouse, food processing workers in Burnaby. Fast placement, WCB compliant.',
    canonical: 'https://worknation.ca/locations/burnaby',
    hero: 'Temp Agency in Burnaby, BC',
    intro: 'Looking for temporary workers in Burnaby? WorkNation Labour Ltd provides staffing solutions for employers across Burnaby and the surrounding Tri-Cities area. With major construction projects around Metrotown and Brentwood, plus industrial operations along the Lougheed corridor, Burnaby employers need reliable workers — and we deliver.',
    areas: ['Metrotown', 'Brentwood', 'Lougheed', 'Edmonds', 'Deer Lake', 'North Burnaby', 'Burnaby Heights', 'Big Bend'],
    industries: 'Burnaby is a hub for construction, warehouse operations, and food processing in the Lower Mainland. The Big Bend industrial area, Still Creek business park, and Brentwood development zones all rely on temporary labour. WorkNation places workers in all of these areas daily.',
    stats: 'Burnaby\'s rapid development and central location in Metro Vancouver make it a prime market for temporary staffing. From high-rise construction to distribution centres, demand for workers in Burnaby remains consistently high.',
  },
  richmond: {
    city: 'Richmond',
    region: 'Metro Vancouver',
    title: 'Temp Agency Richmond - WorkNation Labour Ltd | Temporary Workers Richmond BC',
    meta: 'Need temp workers in Richmond BC? WorkNation Labour Ltd provides warehouse, food processing, cold storage workers in Richmond. Fast placement, WCB compliant.',
    canonical: 'https://worknation.ca/locations/richmond',
    hero: 'Temp Agency in Richmond, BC',
    intro: 'Need temporary workers in Richmond? WorkNation Labour Ltd provides fast staffing for employers in Richmond\'s warehouse district, food processing facilities, cold storage operations, and construction sites. With proximity to Vancouver International Airport and major distribution hubs, Richmond is one of BC\'s busiest centres for logistics and food processing.',
    areas: ['Bridgeport', 'Steveston', 'Hamilton', 'East Richmond', 'City Centre', 'Ironwood', 'Broadmoor', 'Mitchell Island'],
    industries: 'Richmond\'s economy is driven by airport logistics, food processing, cold storage, and warehousing. The Bridgeport and Mitchell Island industrial areas house major distribution centres that require reliable temporary labour year-round. WorkNation is a trusted staffing partner for Richmond employers.',
    stats: 'Richmond\'s unique position as a logistics gateway — with the airport, port connections, and major highways — creates constant demand for warehouse workers, forklift operators, food handlers, and drivers.',
  },
  delta: {
    city: 'Delta',
    region: 'South Fraser',
    title: 'Temp Agency Delta - WorkNation Labour Ltd | Temporary Workers Delta BC',
    meta: 'Need temp workers in Delta BC? WorkNation Labour Ltd provides warehouse, agriculture, cold storage workers in Delta. Tsawwassen, Ladner, North Delta. Contact us.',
    canonical: 'https://worknation.ca/locations/delta',
    hero: 'Temp Agency in Delta, BC',
    intro: 'Looking for temporary workers in Delta? WorkNation Labour Ltd provides staffing for employers in Tsawwassen, Ladner, and North Delta. Delta\'s agricultural lands, warehouse operations, and proximity to the US border crossing make it a key region for seasonal and logistics labour in BC.',
    areas: ['Tsawwassen', 'Ladner', 'North Delta', 'Annacis Island', 'Tilbury Industrial', 'Boundary Bay'],
    industries: 'Delta is home to Annacis Island — one of the largest industrial areas in BC — along with the Tilbury industrial zone and significant agricultural operations. Employers in Delta rely on WorkNation for warehouse workers, food processing staff, farm labourers, and transportation workers.',
    stats: 'Delta\'s industrial zones, agricultural sector, and BC Ferries terminal create diverse staffing needs. From seasonal harvest workers to year-round warehouse staff, Delta employers count on fast, reliable labour.',
  },
  langley: {
    city: 'Langley',
    region: 'Fraser Valley',
    title: 'Temp Agency Langley - WorkNation Labour Ltd | Temporary Workers Langley BC',
    meta: 'Need temp workers in Langley BC? WorkNation Labour Ltd provides construction, warehouse, agriculture workers in Langley. Fast placement, WCB compliant.',
    canonical: 'https://worknation.ca/locations/langley',
    hero: 'Temp Agency in Langley, BC',
    intro: 'Need temporary workers in Langley? WorkNation Labour Ltd provides staffing solutions for employers in both the City of Langley and Township of Langley. With a mix of construction, agriculture, warehousing, and food processing, Langley is one of the Fraser Valley\'s most active labour markets.',
    areas: ['City of Langley', 'Willoughby', 'Walnut Grove', 'Brookswood', 'Murrayville', 'Aldergrove', 'Fort Langley', 'Gloucester Industrial'],
    industries: 'Langley\'s growing residential construction, agricultural operations, and expanding industrial parks drive consistent demand for temporary workers. WorkNation supplies construction labourers, farm workers, warehouse staff, and food processing personnel throughout Langley.',
    stats: 'Langley is one of the fastest-growing communities in the Fraser Valley, with new residential developments, industrial expansion, and a thriving agricultural sector creating abundant opportunities for temporary staffing.',
  },
  abbotsford: {
    city: 'Abbotsford',
    region: 'Fraser Valley',
    title: 'Temp Agency Abbotsford - WorkNation Labour Ltd | Temporary Workers Abbotsford BC',
    meta: 'Need temp workers in Abbotsford BC? WorkNation Labour Ltd provides agriculture, food processing, warehouse workers in Abbotsford. Fast placement, WCB compliant.',
    canonical: 'https://worknation.ca/locations/abbotsford',
    hero: 'Temp Agency in Abbotsford, BC',
    intro: 'Looking for temporary workers in Abbotsford? WorkNation Labour Ltd provides staffing for employers in Abbotsford and the eastern Fraser Valley. Abbotsford is the agricultural capital of British Columbia, with massive food processing operations, cold storage facilities, and growing construction and logistics sectors.',
    areas: ['Downtown Abbotsford', 'Clearbrook', 'Matsqui', 'Aberdeen', 'Mt Lehman', 'Sumas Prairie', 'Abbotsford Airport Area'],
    industries: 'Abbotsford\'s economy revolves around agriculture and food processing. Berry farms, dairy operations, poultry processing, and cold storage facilities all need reliable seasonal and year-round workers. WorkNation also supplies construction and warehouse workers for Abbotsford\'s growing urban development.',
    stats: 'As BC\'s agricultural hub, Abbotsford sees massive seasonal demand for workers during harvest season (May-October) and consistent year-round demand for food processing, cold storage, and warehouse staff.',
  },
  coquitlam: {
    city: 'Coquitlam',
    region: 'Tri-Cities',
    title: 'Temp Agency Coquitlam - WorkNation Labour Ltd | Temporary Workers Coquitlam BC',
    meta: 'Need temp workers in Coquitlam BC? WorkNation Labour Ltd provides construction, warehouse workers in Coquitlam, Port Coquitlam, Port Moody. Fast placement.',
    canonical: 'https://worknation.ca/locations/coquitlam',
    hero: 'Temp Agency in Coquitlam & Tri-Cities, BC',
    intro: 'Need temporary workers in Coquitlam, Port Coquitlam, or Port Moody? WorkNation Labour Ltd provides fast, reliable staffing for employers across the Tri-Cities region. With booming residential construction and expanding industrial areas, the Tri-Cities is one of Metro Vancouver\'s fastest-growing labour markets.',
    areas: ['Coquitlam Centre', 'Burke Mountain', 'Maillardville', 'Port Coquitlam', 'Port Moody', 'Westwood Plateau', 'Austin Heights'],
    industries: 'The Tri-Cities area is experiencing rapid residential and commercial development, with major construction projects across Burke Mountain, Coquitlam Centre, and Port Coquitlam. Warehouse and logistics operations along the Lougheed Highway also drive demand for temporary workers.',
    stats: 'The Tri-Cities region\'s rapid growth, fueled by transit expansion and residential development, creates strong and growing demand for construction labourers, warehouse workers, and general temporary staff.',
  },
};

const LocationPage = ({ locationKey }) => {
  const navigate = useNavigate();
  const loc = locationData[locationKey];

  if (!loc) return null;

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{loc.title}</title>
        <meta name="description" content={loc.meta} />
        <link rel="canonical" href={loc.canonical} />
        <meta property="og:title" content={loc.title} />
        <meta property="og:description" content={loc.meta} />
      </Helmet>
      <Header />
      <main>
        <section className="pt-32 pb-16 px-6 bg-gradient-to-br from-blue-50 via-white to-gray-50">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center mb-4">
              <MapPin className="text-[#003366] mr-3" size={36} aria-hidden="true" />
              <span className="text-[#003366] font-semibold text-lg uppercase tracking-wide">{loc.region}</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-[#003366] mb-6">{loc.hero}</h1>
            <p className="text-lg text-gray-700 leading-relaxed max-w-3xl mb-8">{loc.intro}</p>
            <Button onClick={() => navigate('/employers')} className="bg-[#003366] hover:bg-[#004488] text-white px-8 py-6 text-lg">
              Get Workers in {loc.city} Now <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </section>

        <section className="py-16 px-6 bg-white">
          <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold text-[#003366] mb-6">Areas We Serve in {loc.city}</h2>
              <ul className="space-y-3">
                {loc.areas.map((area, i) => (
                  <li key={i} className="flex items-center text-gray-700">
                    <CheckCircle className="text-green-500 mr-3 flex-shrink-0" size={20} aria-hidden="true" />
                    {area}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="text-3xl font-bold text-[#003366] mb-6">Industries We Staff in {loc.city}</h2>
              <ul className="space-y-3">
                {['Construction & Trades', 'Warehouse & Fulfillment', 'Food Processing', 'Agriculture', 'Transportation & Drivers', 'Cold Storage'].map((ind, i) => (
                  <li key={i} className="flex items-center text-gray-700">
                    <CheckCircle className="text-[#003366] mr-3 flex-shrink-0" size={20} aria-hidden="true" />
                    {ind}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className="py-16 px-6 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-[#003366] mb-6">Staffing Solutions in {loc.city}</h2>
            <p className="text-gray-700 leading-relaxed mb-4">{loc.industries}</p>
            <p className="text-gray-700 leading-relaxed mb-4">{loc.stats}</p>
            <p className="text-gray-700 leading-relaxed">
              WorkNation Labour Ltd handles all payroll, WCB compliance, and administrative requirements for workers placed in {loc.city}. You get reliable, qualified staff on-site — we handle the rest. No hidden fees. No long-term contracts. Just the workers you need, when you need them.
            </p>
          </div>
        </section>

        <section className="py-16 px-6 bg-white">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-[#003366] mb-6 text-center">Request Workers in {loc.city}</h2>
            <ContactForm type="employer" />
          </div>
        </section>

        {/* Other Locations */}
        <section className="py-12 px-6 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-bold text-[#003366] mb-6 text-center">We Also Serve</h2>
            <div className="flex flex-wrap justify-center gap-3">
              {Object.entries(locationData).filter(([key]) => key !== locationKey).map(([key, data]) => (
                <button
                  key={key}
                  onClick={() => navigate(`/locations/${key}`)}
                  className="px-4 py-2 bg-white border border-gray-200 text-[#003366] rounded-lg font-medium hover:bg-[#003366] hover:text-white transition-colors duration-200"
                >
                  {data.city}
                </button>
              ))}
            </div>
          </div>
        </section>

        <CTABanner />
      </main>
      <Footer />
    </div>
  );
};

export { locationData };
export default LocationPage;
